import { NOT_FOUND } from '@jira/platform__http-status-codes/src/http-status-codes.tsx';
import type { ViewAccessLevel } from '@jira/polaris__domain-view/src/ViewAccessLevel.types.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { getJpdViewsServicePathPrefix } from '@jira/polaris__lib-jpd-views-service/src/index.tsx';
import { wrapPromiseWithFetchInteraction } from '@jira/polaris__lib-analytics/src/wrapPromiseWithFetchInteraction.tsx';
import type { Permission, PermissionsResponse } from './PermissionsResponse.types.tsx';
import { getEarlyFetchResponse } from './runGetPermissionsEarlyScript.tsx';
import { getPermissionsUrl } from './getPermissionsUrl.tsx';

const getPermissions = (projectId: number): Promise<PermissionsResponse> => {
	// Check if we have early fetched data
	const cachedPromise = getEarlyFetchResponse(projectId);

	if (cachedPromise) {
		return wrapPromiseWithFetchInteraction(cachedPromise, 'earlyScript/getPermissions').catch(
			() => {
				// If the cached promise fails, fall back to a regular fetch
				return fetchJson(getPermissionsUrl(projectId));
			},
		);
	}

	// If no early fetched data is available, perform the fetch
	return fetchJson(getPermissionsUrl(projectId));
};

// If the API returns 404 (project not available for user (permissions), we do not throw an error
export const fetchPermissions = (projectId: number): Promise<Permission[]> =>
	getPermissions(projectId)
		.then((permissionsResponse) =>
			Object.keys(permissionsResponse.permissions).map((item) => ({
				key: permissionsResponse.permissions[item].key,
				havePermission: permissionsResponse.permissions[item].havePermission,
			})),
		)
		.catch((error) => {
			if (error.statusCode === NOT_FOUND) {
				return [];
			}
			throw error;
		});

export const fetchCreateProjectPermission = (): Promise<boolean> =>
	fetchJson('/rest/api/3/mypermissions?permissions=CREATE_PROJECT')
		.then((response) => {
			return response.permissions.CREATE_PROJECT.havePermission;
		})
		.catch((error) => {
			if (error.statusCode === NOT_FOUND) {
				return false;
			}
			throw error;
		});

export type FetchCurrentUserAccess = {
	accessLevel: ViewAccessLevel;
	permissions: Record<'edit_view' | 'read_view', boolean>;
};

export const fetchViewPermissionsForCurrentUser = async (
	viewId: string,
): Promise<{
	canView: boolean;
	canEdit: boolean;
	accessLevel: ViewAccessLevel;
}> => {
	const response = await fetchJson<FetchCurrentUserAccess>(
		`${getJpdViewsServicePathPrefix()}/v1/views/${viewId}/access/my-access`,
	);

	if (!response.permissions.read_view && !response.permissions.edit_view) {
		throw new Error(
			'polaris.permissions-store.fetch-view-permissions-for-current-user: invalid response',
		);
	}

	return {
		canView: response.permissions.read_view,
		canEdit: response.permissions.edit_view,
		accessLevel: response.accessLevel,
	};
};
